import { saveSmartProfile } from '../components/funtionsBD/digipingSmartService';

const LS_KEY = 'digipingWizardDraft';

export function isResumableWizardPhase(phase) {
  const p = String(phase || '').toLowerCase();
  return !p || p === 'intro' || p === 'capture' || p === 'assessment';
}

export function compactMessageLog(messages) {
  return (Array.isArray(messages) ? messages : [])
    .slice(-60)
    .map((m) => ({
      id: m.id,
      sender: m.sender,
      text: m.text,
      dimension: m.dimension || null,
      step: m.step || null,
      isSystem: m.isSystem || false
    }));
}

export function buildWizardDraftPayload(smartState, messages, { lang = 'en' } = {}) {
  const phase =
    smartState.phase === 'intro' && smartState.slots?.S?.purposeId
      ? 'capture'
      : smartState.phase;
  return {
    slots: smartState.slots,
    status: smartState.status,
    activities: smartState.activities,
    coachAgreement: smartState.coachAgreement,
    activeDimension: smartState.activeDimension || 'S',
    phase: phase === 'active' ? 'capture' : phase,
    pathMeta: {
      wizardDraft: {
        captureStep: smartState.captureStep,
        phase,
        messageLog: compactMessageLog(messages),
        updatedAt: new Date().toISOString()
      }
    },
    lang: String(lang || 'en').substring(0, 2)
  };
}

export function saveWizardDraftLocal(smartState, messages) {
  try {
    const payload = buildWizardDraftPayload(smartState, messages);
    localStorage.setItem(
      LS_KEY,
      JSON.stringify({
        ...payload,
        savedAt: new Date().toISOString()
      })
    );
  } catch (_) {
    /* quota / private mode */
  }
}

export function loadWizardDraftLocal() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

export function clearWizardDraftLocal() {
  try {
    localStorage.removeItem(LS_KEY);
  } catch (_) {
    /* ignore */
  }
}

/**
 * Persist in-progress wizard to API (authenticated) and localStorage (always).
 */
export async function persistWizardDraft(smartState, messages, { lang = 'en', hasToken = false } = {}) {
  if (!smartState?.slots) return;
  if (smartState.phase === 'active') return;

  saveWizardDraftLocal(smartState, messages);

  if (!hasToken) return;

  const payload = buildWizardDraftPayload(smartState, messages, { lang });
  try {
    await saveSmartProfile(payload);
  } catch (err) {
    console.warn('Wizard draft persist failed:', err?.message || err);
  }
}

/** Clear draft after successful profiling completion. */
export async function clearWizardDraft({ hasToken = false, lang = 'en' } = {}) {
  clearWizardDraftLocal();
  if (!hasToken) return;
  try {
    await saveSmartProfile({
      pathMeta: { wizardDraft: null },
      lang: String(lang || 'en').substring(0, 2)
    });
  } catch (_) {
    /* ignore */
  }
}

export function extractResumeDraft(profile, localDraft = null) {
  if (!profile && !localDraft) return null;

  const serverDraft = profile?.pathMeta?.wizardDraft;
  const phase = profile?.phase || localDraft?.phase || serverDraft?.phase;
  const slots = profile?.slots || localDraft?.slots;
  const hasProgress =
    serverDraft?.messageLog?.length ||
    localDraft?.pathMeta?.wizardDraft?.messageLog?.length ||
    slots?.S?.purposeId;

  if (!hasProgress) return null;
  if (profile?.phase === 'active' && profile?.status?.S === 'complete' && !serverDraft?.messageLog?.length) {
    return null;
  }
  if (profile && !isResumableWizardPhase(phase) && !serverDraft) return null;

  const messageLog =
    serverDraft?.messageLog ||
    localDraft?.pathMeta?.wizardDraft?.messageLog ||
    localDraft?.messageLog ||
    [];

  return {
    slots: profile?.slots || localDraft?.slots,
    status: profile?.status || localDraft?.status,
    activities: profile?.activities || localDraft?.activities || [],
    coachAgreement: profile?.coachAgreement || localDraft?.coachAgreement,
    activeDimension: profile?.activeDimension || localDraft?.activeDimension || 'S',
    phase: serverDraft?.phase || phase || 'capture',
    captureStep: serverDraft?.captureStep || localDraft?.captureStep,
    messageLog
  };
}
